import { throttle } from 'lodash';
import { useCallback, useState } from 'react';
import { MapLayerMouseEvent } from 'react-map-gl';
import { usePointStore } from '../../../../store/pointStore';
import { normalizeLongitude } from '../../../../utils/normalizedLongitude';
import {
  FWELAYER,
  INSITU_EXPANDED_LAYER,
  INSITU_LAYER,
  INSITU_UPLOAD_LAYER,
} from '../Layer/MarkerLayer';

export type LayerId =
  | typeof FWELAYER
  | typeof INSITU_LAYER
  | typeof INSITU_UPLOAD_LAYER
  | typeof INSITU_EXPANDED_LAYER
  | 'icon-transparent'
  | 'icon-layer';

export interface LayerInfo {
  layer?: {
    id: LayerId;
  };
  object?: {
    active: boolean;
    coordinates: [number, number];
    [key: string]: any;
  };
  coordinate?: number[];
}

interface UseMapInteractionsProps {
  setActivePopupPoint: (layerId: LayerId) => void;
  onPopupCoordinatesChanged?: (object: any, layerId: LayerId) => void;
  dragPan: boolean;
  isAddMarkerMode: boolean;
  isMeasureMode: boolean;
  onMeasurementPoint?: (point: [number, number]) => void;
}

interface UseMapInteractionsReturn {
  isHoverFweInsitu: boolean;
  mouseCoords?: [number, number];
  mousePosition?: [number, number];
  hoverInfoActivePoint: any | null;
  handleMouseMove: (e: MapLayerMouseEvent) => void;
  handleHover: (info: any) => void;
  getCursor: () => string;
  handleClick: (
    info: any,
    ev: any,
    coords?: [number, number],
    onClick?: (coordinate: number[]) => void,
    isAddMarkerMode?: boolean
  ) => void;
}

export function useMapInteractions(
  props: UseMapInteractionsProps
): UseMapInteractionsReturn {
  const {
    setActivePopupPoint,
    onPopupCoordinatesChanged,
    isAddMarkerMode,
    dragPan,
    isMeasureMode,
    onMeasurementPoint,
  } = props;

  const [isHoverFweInsitu, setIsHoverFweInsitu] = useState<boolean>(false);
  const [hoverInfoActivePoint, setHoverInfoActivePoint] = useState<any | null>(
    null
  );
  const [mouseCoords, setMouseCoords] = useState<[number, number]>();
  const [mousePosition, setMousePosition] = useState<[number, number]>();
  const inSituPoints = usePointStore((state) => state.inSituPoints);

  const isSameCoordinates = (
    first: [number, number],
    second: [number, number],
    precision = 1e-6
  ) => {
    return (
      Math.abs(first[0] - second[0]) <= precision &&
      Math.abs(first[1] - second[1]) <= precision
    );
  };

  const handleMouseMove = useCallback(
    (e: MapLayerMouseEvent): void => {
      if (e.lngLat) {
        const lng = normalizeLongitude(e.lngLat.lng);
        const normalizedLng = Math.round(lng * 10000) / 10000;
        const lat: number = Math.round(e.lngLat.lat * 10000) / 10000;
        setMouseCoords([normalizedLng, lat]);
      }

      if (e.point) {
        setMousePosition([e.point.x, e.point.y]);
      }
    },
    [setMouseCoords, setMousePosition]
  );

  const throttledHoverHandler = throttle(
    (
      info: LayerInfo,
      setHoverState: React.Dispatch<React.SetStateAction<boolean>>,
      setPointState: React.Dispatch<React.SetStateAction<any | null>>,
      dragPanEnabled: boolean
    ): void => {
      const layerId = info?.layer?.id;
      const isActivePoint =
        (layerId === 'icon-transparent' || layerId === 'icon-layer') &&
        info?.object?.active;

      setHoverState(
        layerId === INSITU_LAYER ||
          layerId === FWELAYER ||
          layerId === INSITU_EXPANDED_LAYER
      );

      if (isActivePoint && dragPanEnabled) {
        setPointState(info?.object);
      } else {
        setPointState(null);
      }
    },
    100
  );

  const handleHover = useCallback(
    (info: LayerInfo): void => {
      throttledHoverHandler(
        info,
        setIsHoverFweInsitu,
        setHoverInfoActivePoint,
        dragPan
      );
    },
    [dragPan, throttledHoverHandler]
  );

  const handleClick = useCallback(
    (
      info: LayerInfo,
      ev: any,
      coords?: [number, number],
      onClick?: (coordinate: number[]) => void,
      isAddMarkerMode?: boolean
    ): void => {
      if (!ev) return;

      const clickCoords =
        (info?.coordinate as [number, number] | undefined) ?? coords;

      if (isMeasureMode && clickCoords) {
        if (onClick) {
          onClick(clickCoords);
        } else {
          onMeasurementPoint?.(clickCoords);
        }
        return;
      }

      if (isAddMarkerMode && !isHoverFweInsitu && onClick && clickCoords) {
        onClick(clickCoords);
        return;
      }

      const object = info?.object;
      const layerId = info?.layer?.id;

      if (
        (layerId === 'icon-layer' || layerId === 'icon-transparent') &&
        object?.coordinates
      ) {
        const markerCoordinates = object.coordinates as [number, number];
        const markerInSituName = object.insituPointName as string | undefined;

        const matchingUserUploadedInSitu = inSituPoints.find((point) => {
          const isDeleteEnabled = point.type === 'mood';

          if (!isDeleteEnabled) {
            return false;
          }

          if (markerInSituName) {
            return point.name === markerInSituName;
          }

          return isSameCoordinates(
            point.position as [number, number],
            markerCoordinates
          );
        });

        if (matchingUserUploadedInSitu) {
          setActivePopupPoint(INSITU_LAYER);
          onPopupCoordinatesChanged?.(matchingUserUploadedInSitu, INSITU_LAYER);
          return;
        }
      }

      if (
        layerId === FWELAYER ||
        layerId === INSITU_LAYER ||
        layerId === INSITU_UPLOAD_LAYER ||
        layerId === INSITU_EXPANDED_LAYER
      ) {
        setActivePopupPoint(layerId);
        if (onPopupCoordinatesChanged && object) {
          onPopupCoordinatesChanged(object, layerId);
        }
      }
    },
    [
      isMeasureMode,
      onMeasurementPoint,
      isHoverFweInsitu,
      inSituPoints,
      setActivePopupPoint,
      onPopupCoordinatesChanged,
    ]
  );

  const getCursor = () => {
    if (!dragPan) {
      return 'grabbing';
    }

    if (isMeasureMode) {
      return 'crosshair';
    }

    if (isAddMarkerMode || isHoverFweInsitu) {
      return 'pointer';
    } else if (hoverInfoActivePoint && dragPan) {
      return 'grab';
    }

    return 'default';
  };

  return {
    isHoverFweInsitu,
    hoverInfoActivePoint,
    handleMouseMove,
    handleHover,
    handleClick,
    mouseCoords,
    mousePosition,
    getCursor,
  };
}
